import { computed, type ComputedGetter, type InjectionKey } from 'vue'
import { type NestedKeyOf } from '@/features/shared/types'
import type { IconName } from '@designSystem/fondation/icons/iconsList.ts'
import { isNotNullUndefinedOrEmptyString, objectGetValueWithNestedKey, showBlob } from '@/features/shared/tools'
import type { ModelDemande } from '@/features/domaine/types/opco'
import type { DemandeDTO } from '@/features/api/extranet/dto/Demande/shared'
import type { DemandeStore } from '@/features/interface/demande/shared'
import { type TypeDocumentTemplate, TypeDocumentTemplateNomFichier } from '@/features/domaine/types/opco/shared/TypeDocumentModele.ts'

export type PrintAction = {
  key?: string | number | symbol
  label?: string
  iconName?: IconName
  buttonType?: 'button' | 'icon'
  mustSave?: boolean
  jobs: (()=>Promise<void>) | (()=>Promise<void>)[]
}
export type PrintActions = PrintAction[]

export type UseDemandePrintOptions<TModelDemande extends ModelDemande> = {
  store: DemandeStore<TModelDemande>
  getActions: ComputedGetter<PrintActions>
}

export function useDemandePrint<TModelDemande extends ModelDemande>(options: UseDemandePrintOptions<TModelDemande>) {
  return computed<PrintActions>(() => {
    // Ne pas supprimer cette condition,
    // elle permet de forcer le calcul des actions d'impression
    // aprés le re-calcul des erreurs contenues par les metas
    if (options.store.meta)
      return options.getActions()
    else
      return []
  })
}

export function buildPrintDemandeWithTemplate<TModelDemande extends ModelDemande>(buildOptions: {
  store: DemandeStore<TModelDemande>
  typeDocumentTemplate: TypeDocumentTemplate
  filenameKeys: NestedKeyOf<DemandeDTO<TModelDemande>>[]
}): () => Promise<void> {
  const { typeDocumentTemplate, filenameKeys, store } = buildOptions

  function getFileName() {
    const time = new Date().getTime()
    const [basename, extension] = TypeDocumentTemplateNomFichier[typeDocumentTemplate].split('.')
    const filename = [
      basename,
      ...filenameKeys
        .map((key) => objectGetValueWithNestedKey(store.model!, key))
        .filter(isNotNullUndefinedOrEmptyString)
        .map((value) => `${value}`)
        .map((value) => value.toLowerCase()),
      `${time}`,
    ].join('_')
    return `${filename}.${extension}`
  }

  return async () => {
    const blob = await store.downloadDocumentTemplate(typeDocumentTemplate)
    const filename = getFileName()
    if (blob.size === 0){
      console.warn(`Impression annulée à cause d'un fichier vide : ${filename}`)
      return
    }
    await showBlob(blob, filename)
  }
}

export const DEMANDE_PRINT_ACTIONS: InjectionKey<ReturnType<typeof useDemandePrint<ModelDemande>>> =
  Symbol('demande-print-actions')
